/* eslint-disable max-lines */
import { ITargetsParams } from '@cian/adfox/build/api';
import {
  calculatedParamsMiddleware,
  IAdFoxBannerTypeParams,
  IAdFoxParams,
  IAdFoxTargets,
  mergeMiddlewares,
} from '@cian/adfox/build/middlewares';
import { AdFoxBanner, IAdFoxBannerContext } from '@cian/adfox/build/react';
import { NoSsr } from '@cian/react-utils';
import classNames from 'classnames';
import * as React from 'react';

import { LazyLoadDeprecated } from './LazyLoadDeprecated';
import * as styles from './index.css';
import { AdFoxBanner as AdFoxBannerNew } from './new';
import { AdFoxBannerType, BANNER_TEMPLATE_TYPE, getBannerByType } from './templates';
import { CommercialMortgageBanner } from './templates/commercial_mortgage/';
import { GoogleDefaultBanner } from './templates/google_default';
import { HTML5Banner } from './templates/html_5';
import { MoreParametersPikBanner } from './templates/more_parameters';
import { MortgageSimpleBannerContainer } from './templates/mortgage';
import { MortgageInListing } from './templates/mortgage_in_listing';
import { MortgageBanner } from './templates/mortgage_in_price';
import { ParallaxBanner } from './templates/parallax';
import { SeoBanner } from './templates/tgb_seo_top/';
import { VideoBanner } from './templates/video';
import { AdFoxContext } from './utils/context';
import { sendStats } from '../../../api/stats';
import { IOffer } from '../../../offer/types';
import { IMakeRequest } from '../../../utils/request';
import { ResidentialNativeTGBBanner } from '../../containers/NativeTGBBanner/ResidentialNativeTGBBanner';

import type { TAdFoxBannerSizeProps } from '@cian/adfox-component';
import type { IWithPostViewAnalyticsPayload } from '@cian/postview-analytics-utils';

export type TAddFoxBannerConfig = {
  type: AdFoxBannerType;
  key: string | number;
  makeRequest?: IMakeRequest;
  offer?: IOffer;
  locationId?: number;
  additionalPuids?: { [key: string]: string | null };
  isAgent?: boolean;
  isNew?: boolean;
  onError?(): void;
  onStub?(): void;
  realtyUserId?: number | null;
  position?: number;
  shouldSendAdsEvent?: boolean;
  ignoreOfferPuids?: boolean;
  postViewAnalyticsData?: IWithPostViewAnalyticsPayload;
} & TAdFoxBannerSizeProps;

interface IAdFoxMiddlewareConfig {
  id?: string;
  type: AdFoxBannerType;
  offer?: IOffer;
  additionalPuids?: { [key: string]: string | null };
  ignoreOfferPuids?: boolean;
}

// Зачение pr должно быть одно для всех баннеров в рамках одной загрузки страницы
const PR_RANDOM = getRandomId();

export function getBannerMiddleware({ type, offer, id, additionalPuids, ignoreOfferPuids }: IAdFoxMiddlewareConfig) {
  return () =>
    bannerMiddleware(
      type,
      Promise.resolve().then(() => {
        const targetParams: ITargetsParams = {};

        if (type === 'video' || type === 'parallax') {
          Object.assign(targetParams, {
            pr: PR_RANDOM,
            puid20: 'abtest',
          });
        } else {
          Object.assign(targetParams, {
            pr: PR_RANDOM,
            pr1: id || getRandomId(),
          });
        }

        if (offer && offer.adfoxParams && !ignoreOfferPuids) {
          Object.assign(targetParams, offer.adfoxParams);
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return Object.assign(targetParams, additionalPuids) as any;
      }),
    );
}

export function addAdFoxBanner(config: TAddFoxBannerConfig) {
  const {
    key,
    locationId,
    makeRequest,
    offer,
    type,
    additionalPuids = { puid6: '' },
    isNew,
    isAgent,
    position,
    realtyUserId,
    onError,
    onStub,
    shouldSendAdsEvent,
    ignoreOfferPuids,
    postViewAnalyticsData,
    ...sizeParameters
  } = config;

  const randomId = getRandomId();

  return getBanner({
    key,
    makeRequest,
    middleware: getBannerMiddleware({ type, additionalPuids, id: randomId, offer, ignoreOfferPuids }),
    locationId,
    randomId,
    position,
    isNew,
    isAgent,
    postViewAnalyticsData,
    onError,
    onStub,
    ...sizeParameters,
  });
}

export function addPikAdfoxBanner(locationId: number, makeRequest: IMakeRequest) {
  const middleware = () =>
    bannerMiddleware(
      'more_parameters_pik',
      Promise.resolve<IAdFoxParams>({
        puid1: 'deal_type_sale',
        puid2: 'offer_flat',
        puid5: 'obl_id_' + locationId,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any),
    );

  return getBanner({
    key: 1,
    makeRequest,
    middleware,
    locationId,
    randomId: getRandomId(),
    width: '100%',
    minHeight: '100px',
    postViewAnalyticsData: {
      banner: {
        place: 'offersSerp',
        name: 'Listing_filter_more',
      },
    },
  });
}

export function getRandomId() {
  return String(Math.floor(Math.random() * 4294967295) + 1);
}

type TGetBannerParameters = {
  key: string | number;
  makeRequest?: IMakeRequest;
  middleware: () => Promise<IAdFoxParams>;
  locationId: number | undefined;
  randomId: string;
  isNew?: boolean;
  isAgent?: boolean;
  position?: number;
  postViewAnalyticsData?: IWithPostViewAnalyticsPayload;
  onBannerLoad?: (context: IAdFoxBannerContext) => void;
  onBannerClick?: () => void;
  onError?: () => void;
  onStub?: () => void;
} & TAdFoxBannerSizeProps;

function getBanner({
  key,
  makeRequest,
  middleware,
  locationId,
  randomId,
  position,
  isNew,
  isAgent,
  postViewAnalyticsData,
  onBannerLoad,
  onBannerClick,
  onStub,
  onError,
  ...sizeParameters
}: TGetBannerParameters): React.ReactNode {
  if (isNew) {
    return (
      <NoSsr key={key} defer>
        <AdFoxContext.Consumer>
          {({ onLoad }) => (
            <LazyLoadDeprecated
              offset={300}
              onLoad={() => {
                onLoad?.();

                return middleware();
              }}
              renderLoaded={(result: IAdFoxParams) => {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { pr, pr1, ...rest } = result;

                return (
                  <AdFoxBannerNew
                    {...sizeParameters}
                    ownerId={202100}
                    position={position}
                    disableYan={isAgent}
                    onError={onError}
                    onStub={onStub}
                    postViewAnalyticsData={postViewAnalyticsData}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    params={rest as any}
                  />
                );
              }}
              renderEmpty={() => {
                return getBannerMarkup(false, randomId, <span />);
              }}
            />
          )}
        </AdFoxContext.Consumer>
      </NoSsr>
    );
  }

  return (
    <NoSsr key={key} defer>
      <AdFoxContext.Consumer>
        {({ onLoad }) => (
          <LazyLoadDeprecated
            offset={300}
            onLoad={() => {
              onLoad?.();

              return middleware();
            }}
            renderLoaded={(result: IAdFoxParams) => (
              <AdFoxBanner
                middleware={() => Promise.resolve(result)}
                renderFailure={error => {
                  let errorType = 'template-error';

                  if (error.message === 'Unpossible to load script') {
                    errorType = !window._cianCanRunAds ? 'request-error' : 'adblock-error';
                  }

                  if (makeRequest) {
                    sendStats(makeRequest, errorType, key.toString());
                  }

                  return getBannerMarkup(false, randomId, <span />, isAgent);
                }}
                renderFetched={(context: IAdFoxBannerContext) => {
                  if (!!onBannerLoad && context['type'] !== 'nativeTGB') {
                    onBannerLoad(context);
                  }

                  switch (context['type'] as BANNER_TEMPLATE_TYPE) {
                    case 'nativeTGB':
                      return getBannerMarkup(
                        true,
                        randomId,
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        <ResidentialNativeTGBBanner context={context as any} locationId={locationId} />,
                        isAgent,
                      );
                    case 'mortgage_banner':
                      return getBannerMarkup(
                        true,
                        randomId,
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        <MortgageSimpleBannerContainer context={context as any} />,
                        isAgent,
                      );
                    case 'mortgage':
                      return getBannerMarkup(true, randomId, <MortgageBanner context={context} />, isAgent);
                    case 'commercial_mortgage':
                      return getBannerMarkup(true, randomId, <CommercialMortgageBanner context={context} />, isAgent);
                    case 'seo':
                      return getBannerMarkup(true, randomId, <SeoBanner context={context} />, isAgent, onBannerClick);
                    case 'video':
                      return getBannerMarkup(true, randomId, <VideoBanner context={context} />, isAgent, onBannerClick);
                    case 'parallax':
                      return getBannerMarkup(
                        true,
                        randomId,
                        <ParallaxBanner context={context} />,
                        isAgent,
                        onBannerClick,
                      );
                    case 'more_parameters':
                      return getBannerMarkup(
                        true,
                        randomId,
                        <MoreParametersPikBanner context={context} />,
                        isAgent,
                        onBannerClick,
                      );
                    case 'stretch':
                      return getBannerMarkup(true, randomId, <HTML5Banner context={context} />, isAgent, onBannerClick);
                    case 'google_default':
                      return getBannerMarkup(
                        true,
                        randomId,
                        <GoogleDefaultBanner context={context} />,
                        isAgent,
                        onBannerClick,
                      );
                    case 'mortgage_in_listing':
                      return getBannerMarkup(true, randomId, <MortgageInListing context={context} />, isAgent);
                    default:
                      return getBannerMarkup(false, randomId, <span />, isAgent);
                  }
                }}
              />
            )}
            renderEmpty={() => {
              return getBannerMarkup(false, randomId, <span />, isAgent);
            }}
          />
        )}
      </AdFoxContext.Consumer>
    </NoSsr>
  );
}

function getBannerMarkup(
  isValidBanner: boolean,
  randomId: string,
  markup: JSX.Element,
  isAgent?: boolean,
  onBannerClick?: () => void,
) {
  return (
    <div
      className={classNames(styles['simplified-banner'], !isValidBanner && styles['no-banner'])}
      key={randomId}
      onClick={onBannerClick}
    >
      <div>{markup}</div>
      <div id={`AdFox_banner_${randomId}`} />
    </div>
  );
}

function bannerMiddleware(bannerType: AdFoxBannerType, request: Promise<IAdFoxTargets>): Promise<IAdFoxParams> {
  return mergeMiddlewares(request, typeParamsMiddleware(bannerType), calculatedParamsMiddleware());
}

function typeParamsMiddleware(type: AdFoxBannerType): Promise<IAdFoxBannerTypeParams> {
  return Promise.resolve(getBannerByType(type));
}
